<template>
  <div>
    <lf-checkbox-dropdown
      :model-value="modelValue"
      :options="userOptions"
      :name="name"
      :enable-search="enableSearch"
      :show-clear-filter="showClearFilter"
      :filter-icon="filterIcon"
      :is-loading="isRequestLoading"
      v-bind="omit($attrs, ['is-loading'])"
      @update:model-value="emit('update:modelValue', $event)"
      @change-list-visibility="isFilterShown = $event"
    />
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import omit from "lodash/omit";

import { useUsersByRole } from "@/hooks/queries/useUsers";

import LfCheckboxDropdown from "@/components/ui/inputs/LfCheckboxDropdown.vue";

import type { Role } from "@/models/options";

const emit = defineEmits<{
  "update:modelValue": unknown[];
}>();

const {
  modelValue,
  name,
  enableSearch,
  showClearFilter,
  filterIcon,
  showCurrentUserOption,
} = defineProps<{
  modelValue: number[];
  name: Role;
  enableSearch: boolean;
  showClearFilter: boolean;
  filterIcon: boolean;
  showCurrentUserOption?: boolean;
}>();

const { t } = useI18n();

const isFilterShown = ref(false);

// Enable query if: always (when no current user option), dropdown is shown, or has pre-selected values
const shouldLoadUsers = computed(
  () => !showCurrentUserOption || isFilterShown.value || !!modelValue.length
);

const { data, isLoading: isRequestLoading } = useUsersByRole(name, {
  ignoreAssignedDeals: false,
  enabled: shouldLoadUsers,
});

const userOptions = computed(() => {
  const defaultOptions: Record<string, string> = { null: t("COMMON.NONE") };

  if (showCurrentUserOption) {
    defaultOptions[-1] = t("COMMON.CURRENT_USER");
  }
  return (
    data.value?.reduce((acc: Record<string, string>, user) => {
      if (!user.id) {
        return acc;
      }

      Object.assign(acc, { [user.id]: user.full_name });
      return acc;
    }, defaultOptions) ?? defaultOptions
  );
});
</script>
